import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import { parseReportingAnalysisSnapshot } from 'src/queries/reporting/reporting.validation'
import type {
  ReportingMessage,
  ReportingMessagePage,
  ReportingOverviewSpendChart,
  ReportingResolvedChartContext,
} from 'src/queries/reportingAgent/reportingAgent.models'
import { REPORTING_AGENT_VERSION } from 'src/queries/reportingAgent/reportingAgent.models'

import {
  exact,
  isoDate,
  nullableIdentifier,
  object,
  string,
  version,
} from './primitives'

const message = (value: unknown, index: number): ReportingMessage => {
  const source = object(value, `messages.items[${index}]`)
  exact(
    source,
    [
      'version',
      'id',
      'conversation_id',
      'role',
      'content',
      'run_id',
      'chart_context',
      'created_at',
    ],
    `messages.items[${index}]`,
  )
  version(source.version, `messages.items[${index}]`)
  if (source.role !== 'user' && source.role !== 'assistant')
    throw new Error('message.role is invalid')
  return {
    version: REPORTING_AGENT_VERSION,
    id: reportingIdentifier(source.id, 'message.id'),
    conversation_id: reportingIdentifier(
      source.conversation_id,
      'message.conversation_id',
    ),
    role: source.role,
    content: string(source.content, 'message.content'),
    run_id: nullableIdentifier(source.run_id, 'message.run_id'),
    chart_context:
      source.chart_context === null
        ? null
        : parseResolvedChartContext(
            source.chart_context,
            `messages.items[${index}].chart_context`,
          ),
    created_at: isoDate(source.created_at, 'message.created_at'),
  }
}

const parseResolvedChartContext = (value: unknown, name: string) => {
  const source = object(value, name)
  exact(
    source,
    [
      'version',
      'chart_context_id',
      'source_kind',
      'title',
      'context_label',
      'anchor_ref',
      'generated_at',
      'presentation',
    ],
    name,
  )
  version(source.version, name)
  if (
    source.source_kind !== 'overview' &&
    source.source_kind !== 'live_report' &&
    source.source_kind !== 'artifact'
  ) {
    throw new Error(`${name}.source_kind is invalid`)
  }
  const sourceKind =
    source.source_kind as ReportingResolvedChartContext['source_kind']
  const anchor = object(source.anchor_ref, `${name}.anchor_ref`)
  exact(anchor, ['anchor_id', 'anchor_revision'], `${name}.anchor_ref`)
  if (
    !Number.isInteger(anchor.anchor_revision) ||
    Number(anchor.anchor_revision) < 1
  )
    throw new Error(`${name}.anchor_ref.anchor_revision is invalid`)
  const presentation = parseResolvedChartPresentation(
    source.presentation,
    `${name}.presentation`,
  )
  if ((sourceKind === 'overview') !== (presentation.kind === 'overview')) {
    throw new Error(`${name}.source_kind does not match its presentation`)
  }
  return {
    version: REPORTING_AGENT_VERSION,
    chart_context_id: reportingIdentifier(
      source.chart_context_id,
      `${name}.chart_context_id`,
    ),
    source_kind: sourceKind,
    title: string(source.title, `${name}.title`),
    context_label: string(source.context_label, `${name}.context_label`),
    anchor_ref: {
      anchor_id: reportingIdentifier(
        anchor.anchor_id,
        `${name}.anchor_ref.anchor_id`,
      ),
      anchor_revision: Number(anchor.anchor_revision),
    },
    generated_at: isoDate(source.generated_at, `${name}.generated_at`),
    presentation,
  }
}

const OVERVIEW_CHARTS = new Set([
  'total_company_spend',
  'spend_by_accounting_category',
  'spend_by_department',
  'spend_by_vendor',
])
const CURRENCIES = new Set([
  'AUD',
  'CAD',
  'CHF',
  'EUR',
  'GBP',
  'HKD',
  'JPY',
  'MXN',
  'NZD',
  'SGD',
  'USD',
])
const ISO_DATE_PATTERN = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/

const nullableString = (value: unknown, name: string): string | null =>
  value === null ? null : string(value, name)

const isoDay = (value: unknown, name: string): string => {
  const result = string(value, name)
  const parsed = new Date(`${result}T00:00:00Z`)
  if (
    !ISO_DATE_PATTERN.test(result) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== result
  )
    throw new Error(`${name} must be a canonical date`)
  return result
}

const parseOverviewMetadata = (
  value: unknown,
  name: string,
): Record<string, string | null> => {
  const source = object(value, name)
  return Object.fromEntries(
    Object.entries(source).map(([key, item]) => [
      key,
      nullableString(item, `${name}.${key}`),
    ]),
  )
}

const parseResolvedChartPresentation = (
  value: unknown,
  name: string,
): ReportingResolvedChartContext['presentation'] => {
  const source = object(value, name)
  if (source.kind === 'reporting_artifact') {
    exact(source, ['kind', 'chart_spec', 'dataset'], name)
    const snapshot = parseReportingAnalysisSnapshot(
      source.chart_spec,
      source.dataset,
    )
    return { kind: 'reporting_artifact', ...snapshot }
  }
  if (source.kind !== 'overview') throw new Error(`${name}.kind is invalid`)
  exact(source, ['kind', 'spend_chart'], name)
  const chart = object(source.spend_chart, `${name}.spend_chart`)
  exact(
    chart,
    ['chart', 'since', 'until', 'currency', 'points', 'total_spend_cents'],
    `${name}.spend_chart`,
  )
  if (typeof chart.chart !== 'string' || !OVERVIEW_CHARTS.has(chart.chart))
    throw new Error(`${name}.spend_chart.chart is invalid`)
  const since = isoDay(chart.since, `${name}.spend_chart.since`)
  const until = isoDay(chart.until, `${name}.spend_chart.until`)
  if (since >= until) throw new Error(`${name}.spend_chart range is invalid`)
  if (typeof chart.currency !== 'string' || !CURRENCIES.has(chart.currency))
    throw new Error(`${name}.spend_chart.currency is invalid`)
  if (!Array.isArray(chart.points) || chart.points.length > 1000)
    throw new Error(`${name}.spend_chart.points is invalid`)
  const points = chart.points.map((value, index) => {
    const pointName = `${name}.spend_chart.points[${index}]`
    const point = object(value, pointName)
    exact(
      point,
      [
        'label',
        'spend_cents',
        'key',
        'bucket',
        'series_key',
        'series_label',
        'metadata',
      ],
      pointName,
    )
    const label = string(point.label, `${pointName}.label`)
    if (!label || label.length > 256)
      throw new Error(`${pointName}.label is invalid`)
    if (!Number.isSafeInteger(point.spend_cents))
      throw new Error(`${pointName}.spend_cents is invalid`)
    return {
      label,
      spend_cents: Number(point.spend_cents),
      key: nullableString(point.key, `${pointName}.key`),
      bucket:
        point.bucket === null
          ? null
          : isoDay(point.bucket, `${pointName}.bucket`),
      series_key: nullableString(point.series_key, `${pointName}.series_key`),
      series_label: nullableString(
        point.series_label,
        `${pointName}.series_label`,
      ),
      metadata: parseOverviewMetadata(point.metadata, `${pointName}.metadata`),
    }
  })
  if (!Number.isSafeInteger(chart.total_spend_cents))
    throw new Error(`${name}.spend_chart.total_spend_cents is invalid`)
  const totalSpendCents = Number(chart.total_spend_cents)
  const pointTotal = points.reduce(
    (total, point) => total + point.spend_cents,
    0,
  )
  if (!Number.isSafeInteger(pointTotal) || pointTotal !== totalSpendCents)
    throw new Error(`${name}.spend_chart total does not match its points`)
  return {
    kind: 'overview',
    spend_chart: {
      chart: chart.chart as ReportingOverviewSpendChart['chart'],
      since,
      until,
      currency: chart.currency,
      points,
      total_spend_cents: totalSpendCents,
    },
  }
}

export const parseMessagePage = (value: unknown): ReportingMessagePage => {
  const source = object(value, 'messages')
  exact(source, ['version', 'items', 'next_cursor'], 'messages')
  version(source.version, 'messages')
  if (!Array.isArray(source.items))
    throw new Error('messages.items must be an array')
  return {
    version: REPORTING_AGENT_VERSION,
    items: source.items.map(message),
    next_cursor: nullableIdentifier(source.next_cursor, 'messages.next_cursor'),
  }
}
